import { create } from "zustand";
import {
  externalService,
  ExternalCategory,
  ExternalMovie,
  ExternalActor,
  ExternalDirector,
  ExternalCountry,
} from "@/services/externalService";

interface ExternalState {
  // State
  categories: ExternalCategory[];
  movies: ExternalMovie[];
  actors: ExternalActor[];
  directors: ExternalDirector[];
  countries: ExternalCountry[];
  isLoadingCategories: boolean;
  isLoadingMovies: boolean;
  isLoadingActors: boolean;
  isLoadingDirectors: boolean;
  isLoadingCountries: boolean;
  error: string | null;

  // Actions
  fetchCategories: () => Promise<void>;
  fetchMovies: () => Promise<void>;
  fetchActors: () => Promise<void>;
  fetchDirectors: () => Promise<void>;
  fetchCountries: () => Promise<void>;
  clearError: () => void;
}

export const useExternalStore = create<ExternalState>((set) => ({
  // Initial state
  categories: [],
  movies: [],
  actors: [],
  directors: [],
  countries: [],
  isLoadingCategories: false,
  isLoadingMovies: false,
  isLoadingActors: false,
  isLoadingDirectors: false,
  isLoadingCountries: false,
  error: null,

  // Fetch categories
  fetchCategories: async () => {
    set({ isLoadingCategories: true, error: null });
    try {
      const response: any = await externalService.getAllCategories();

      if (response.ok && response.data) {
        set({
          categories: response.data?.data?.data || [],
          isLoadingCategories: false,
        });
      } else {
        set({
          error: response.message || "Lỗi khi tải danh sách categories",
          isLoadingCategories: false,
        });
      }
    } catch (error: any) {
      set({
        error: error.message || "Lỗi khi tải danh sách categories",
        isLoadingCategories: false,
      });
    }
  },

  // Fetch movies
  fetchMovies: async () => {
    set({ isLoadingMovies: true, error: null });
    try {
      const response: any = await externalService.getAllMovies();
      if (response.ok && response.data) {
        set({
          movies: response.data?.data?.data || [],
          isLoadingMovies: false,
        });
      } else {
        set({
          error: response.message || "Lỗi khi tải danh sách movies",
          isLoadingMovies: false,
        });
      }
    } catch (error: any) {
      set({
        error: error.message || "Lỗi khi tải danh sách movies",
        isLoadingMovies: false,
      });
    }
  },

  // Fetch actors
  fetchActors: async () => {
    set({ isLoadingActors: true, error: null });
    try {
      const response: any = await externalService.getAllActors();
      if (response.ok && response.data) {
        set({
          actors: response.data?.data?.data || [],
          isLoadingActors: false,
        });
      } else {
        set({
          error: response.message || "Lỗi khi tải danh sách actors",
          isLoadingActors: false,
        });
      }
    } catch (error: any) {
      set({
        error: error.message || "Lỗi khi tải danh sách actors",
        isLoadingActors: false,
      });
    }
  },

  // Fetch directors
  fetchDirectors: async () => {
    set({ isLoadingDirectors: true, error: null });
    try {
      const response: any = await externalService.getAllDirectors();
      if (response.ok && response.data) {
        set({
          directors: response.data?.data?.data || [],
          isLoadingDirectors: false,
        });
      } else {
        set({
          error: response.message || "Lỗi khi tải danh sách directors",
          isLoadingDirectors: false,
        });
      }
    } catch (error: any) {
      set({
        error: error.message || "Lỗi khi tải danh sách directors",
        isLoadingDirectors: false,
      });
    }
  },

  // Fetch countries
  fetchCountries: async () => {
    set({ isLoadingCountries: true, error: null });
    try {
      const response: any = await externalService.getAllCountries();
      if (response.ok && response.data) {
        set({
          countries: response.data?.data?.data || [],
          isLoadingCountries: false,
        });
      } else {
        set({
          error: response.message || "Lỗi khi tải danh sách countries",
          isLoadingCountries: false,
        });
      }
    } catch (error: any) {
      set({
        error: error.message || "Lỗi khi tải danh sách countries",
        isLoadingCountries: false,
      });
    }
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },
}));
